// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Captures the document version approved for each selected agent run.
 *
 * Version Comparison consumes adjacent versions while ordinary agents consume
 * one version. Keeping that choice explicit before queueing prevents bulk runs
 * from silently applying one incompatible version rule to every agent.
 */
import { useEffect, useState } from 'react'
import { Bot, FileText, Play } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import type { Agent } from '@/subsystems/agents'

import type { DocumentListItemWithPipelines } from '../models'

/** Versions selected per agent, then per document: `versions[agentId][documentId]`. */
export type RunVersions = Record<string, Record<string, number>>

interface WorkspaceRunDialogProps {
  open: boolean
  agents: Agent[]
  documents: DocumentListItemWithPipelines[]
  running: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (versions: RunVersions) => void
}

function agentComparesVersions(agent: Agent) {
  return agent.pipelineId === 'version-diff'
}

function agentMinimumVersion(agent: Agent) {
  return Math.max(agentComparesVersions(agent) ? 2 : 1, agent.documentFilter?.minVersions ?? 1)
}

export function WorkspaceRunDialog({
  open,
  agents,
  documents,
  running,
  onOpenChange,
  onConfirm,
}: WorkspaceRunDialogProps) {
  const [versions, setVersions] = useState<RunVersions>({})
  const agentLabels = agents.map((agent) => agent.label)
  const analysisCount = agents.length * documents.length

  useEffect(() => {
    if (!open) return
    setVersions(Object.fromEntries(agents.map((agent) => [
      agent.agentId,
      Object.fromEntries(documents.map((document) => [document.documentId, document.latestVersion])),
    ])))
  }, [open, agents, documents])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="gap-1.5 px-6 pb-4 pt-6">
          <DialogTitle className="text-xl">
            Select document {documents.length === 1 ? 'version' : 'versions'}
          </DialogTitle>
          <DialogDescription>
            {agents.length === 1
              ? `Choose the document version ${agentLabels[0]} will run on.`
              : 'Choose the version each selected agent will run on.'}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-4">
          <span className="inline-flex items-center gap-2 rounded-full border bg-slate-50 px-3 py-1 text-sm text-slate-600">
            <Bot className="h-4 w-4" />
            {agents.length} {agents.length === 1 ? 'agent' : 'agents'} · {agentLabels.join(', ')}
          </span>
        </div>
        <div className="max-h-96 space-y-4 overflow-y-auto px-6 pb-6">
          {agents.map((agent) => {
            const comparesVersions = agentComparesVersions(agent)
            const minimumVersion = agentMinimumVersion(agent)
            return (
              <div key={agent.agentId} className="space-y-3">
                {agents.length > 1 && (
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Bot className="h-4 w-4 text-slate-500" />
                    {agent.label}
                  </div>
                )}
                {documents.map((document) => (
                  <div
                    key={document.documentId}
                    className="flex items-center gap-4 rounded-xl border bg-slate-50/50 p-4"
                  >
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{document.latestFilename ?? document.name}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">{document.documentId}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                        Version
                      </div>
                      <Select
                        value={String(versions[agent.agentId]?.[document.documentId] ?? document.latestVersion)}
                        onValueChange={(value) => setVersions((current) => ({
                          ...current,
                          [agent.agentId]: {
                            ...current[agent.agentId],
                            [document.documentId]: Number.parseInt(value, 10),
                          },
                        }))}
                      >
                        <SelectTrigger className="w-36 bg-white" aria-label={`Version for ${document.documentId} (${agent.label})`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: document.latestVersion }, (_, index) => document.latestVersion - index)
                            .filter((version) => version >= minimumVersion)
                            .map((version) => (
                              <SelectItem key={version} value={String(version)}>
                                {comparesVersions
                                  ? `v${version} vs v${version - 1}`
                                  : `v${version}${version === document.latestVersion ? ' (latest)' : ''}`}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
        <DialogFooter className="border-t bg-slate-50/60 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onConfirm(versions)} disabled={running}>
            <Play className="h-4 w-4 fill-current" />
            {analysisCount === 1 ? 'Run' : `Run ${analysisCount} analyses`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
